import { computed, ref } from 'vue';
import type { ColorScheme } from '@/types';

const STORAGE_KEY = 'map-kit-color-scheme';

const stored = (typeof localStorage !== 'undefined'
  ? localStorage.getItem(STORAGE_KEY)
  : null) as ColorScheme | null;

const colorScheme = ref<ColorScheme>(stored ?? 'auto');

function applyToDOM() {
  const el = document.documentElement;
  el.classList.remove('color-scheme-dark');
  const effective =
    colorScheme.value === 'auto'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : colorScheme.value;
  if (effective === 'dark') {
    el.classList.add('color-scheme-dark');
  }
}

/**
 * Composable for toggling dark / light / auto color scheme.
 * Mirrors the approach used in the wildfire service frontend.
 */
export function useColorScheme() {
  const setColorScheme = (val: ColorScheme) => {
    colorScheme.value = val;
    localStorage.setItem(STORAGE_KEY, val);
    applyToDOM();
  };

  const realColorScheme = computed<'dark' | 'light'>(() => {
    if (colorScheme.value === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return colorScheme.value;
  });

  // Apply on first import
  applyToDOM();

  return { colorScheme, setColorScheme, realColorScheme };
}
